import {
  AppCard,
  Button,
  FormAsyncAutocomplete,
  FormTextField,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { Box, CircularProgress } from "@mui/material";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { Suspense, useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { Vendor } from "../../../../@types/services/Vendor";
import { useCreateQuotationBatch } from "../../../../__generated__/orval/endpoints/quotation-batches/quotation-batches";
import type { EntitiesQuotationBatchVendor } from "../../../../__generated__/orval/models/entitiesQuotationBatchVendor.zod";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { fetchVendors } from "../../../../services/vendors";
import { PrList, type QuotationListItem } from "./-internal_components/PrList";
import { SuggestedVendors } from "./-internal_components/SuggestedVendors";
import {
  batchVendorsQueryOptions,
  quotationsByIdsQueryOptions,
} from "./-queries/index";

const searchSchema = z.object({
  quotation_ids: z.array(z.coerce.number()).optional().catch(undefined),
});

const DEFAULT_OFFER_SUBMISSION_DAYS = 5;

const QuotationBatchForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { quotation_ids = [] } = Route.useSearch();

  const { data: quotations = [] } = useSuspenseQuery(
    quotationsByIdsQueryOptions(quotation_ids),
  );

  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set());
  const quotationList = quotations.filter((q) => !removedIds.has(q.id));

  const { control, getValues, setValue, watch } = useForm<{
    vendors: Vendor[];
    name: string;
  }>({
    defaultValues: {
      name: "",
      vendors: [],
    },
  });

  const selectedVendors = watch("vendors");

  const quotationParams = useMemo(() => {
    const segmentSet = new Set<number>();
    const materialGroupSet = new Set<number>();
    const brandSet = new Set<number>();

    for (const q of quotationList) {
      if (q.segment?.id) segmentSet.add(q.segment.id);
      if (q.material_group?.id) materialGroupSet.add(q.material_group.id);
      if (q.material?.brand_id) brandSet.add(q.material.brand_id);
    }

    return {
      brands: [...brandSet],
      materialGroups: [...materialGroupSet],
      segments: [...segmentSet],
    };
  }, [quotationList]);

  const { data: batchVendors } = useSuspenseQuery(
    batchVendorsQueryOptions(quotationParams),
  );

  const segmentVendors = batchVendors?.vendor_suggestion_segment || [];
  const materialGroupVendors =
    batchVendors?.vendor_suggestion_material_group || [];
  const brandVendors = batchVendors?.vendor_suggestion_brand || [];

  const { mutate: createBatch, isPending } = useCreateQuotationBatch({
    mutation: {
      onError: (error) => {
        enqueueSnackbar(
          error.message || t("quotationBatch.messages.createError"),
          { variant: "error" },
        );
      },
      onSuccess: (batch) => {
        enqueueSnackbar(t("quotationBatch.messages.createSuccess"), {
          variant: "success",
        });
        queryClient.invalidateQueries({
          queryKey: ["quotationBatches"],
        });
        // TODO: Redirect to Rails page for now; switch to React show page when available.
        window.location.href = `/vbuyer/quotation_batches/${batch.id}`;
      },
    },
  });

  const handleRemoveQuotation = useCallback((id: number) => {
    setRemovedIds((prev) => new Set([...prev, id]));
  }, []);

  const handleAddVendor = useCallback(
    (vendor: EntitiesQuotationBatchVendor) => {
      if (!selectedVendors.find((v) => v.id === vendor.id)) {
        setValue("vendors", [...selectedVendors, vendor as unknown as Vendor]);
      }
    },
    [selectedVendors, setValue],
  );

  const handleSubmit = useCallback(() => {
    const batchName = getValues("name");
    if (!batchName || batchName.trim() === "") {
      enqueueSnackbar(t("quotationBatch.errors.emptyName"), {
        variant: "error",
      });
      return;
    }
    if (quotationList.length === 0) {
      enqueueSnackbar(t("quotationBatch.errors.noQuotations"), {
        variant: "error",
      });
      return;
    }
    if (selectedVendors.length === 0) {
      enqueueSnackbar(t("quotationBatch.errors.emptyVendor"), {
        variant: "error",
      });
      return;
    }
    createBatch({
      data: {
        name: batchName,
        offer_submission_days: DEFAULT_OFFER_SUBMISSION_DAYS,
        quotation_ids: quotationList.map((q) => q.id),
        vendor_ids: selectedVendors.map((v) => v.id),
      },
    });
  }, [createBatch, getValues, quotationList, selectedVendors, t]);

  return (
    <PageContainer showBackButton title={t("quotationBatch.new.title")}>
      <AppCard>
        <PrList
          onRemove={handleRemoveQuotation}
          quotationList={quotationList as unknown as QuotationListItem[]}
        />

        <FormTextField
          control={control}
          fullWidth
          label={t("quotationBatch.name")}
          name="name"
          required
          size="small"
        />

        <FormAsyncAutocomplete
          control={control}
          fetchOptions={fetchVendors}
          filters={(searchTerm: string) => ({
            name: searchTerm,
          })}
          getOptionLabel={(option: Vendor) =>
            option ? option.display_name_with_sap_code_and_tax_number : ""
          }
          isOptionEqualToValue={(option: Vendor, value: string | Vendor) =>
            typeof value !== "string" && option?.id === value?.id
          }
          label={t("common.vendors")}
          minSearchLength={3}
          multiple
          name="vendors"
          queryKey={["vendors", "infinite"]}
        />

        <SuggestedVendors
          brandVendors={brandVendors}
          materialGroupVendors={materialGroupVendors}
          onAddVendor={handleAddVendor}
          segmentVendors={segmentVendors}
          selectedVendors={selectedVendors}
        />

        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button
            disabled={isPending}
            onClick={() =>
              navigate({
                search: { tab: "open" },
                to: "/vbuyer/quotations",
              })
            }
            variant="outlined"
          >
            {t("common.cancel")}
          </Button>
          <Button
            disabled={isPending}
            onClick={handleSubmit}
            variant="contained"
          >
            {isPending ? <CircularProgress size={24} /> : t("common.create")}
          </Button>
        </Box>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/quotation_batches/new",
  // biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
)({
  validateSearch: searchSchema,
  component: () => (
    <Suspense
      fallback={
        <PageContainer showBackButton={false} title="...">
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        </PageContainer>
      }
    >
      <QuotationBatchForm />
    </Suspense>
  ),
  loaderDeps: ({ search }) => ({
    quotationIds: search.quotation_ids || [],
  }),
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Quotation Batch - Create")) {
      return;
    }

    throw new ForbiddenError();
  },
  loader: ({ context, deps }) => {
    if (deps.quotationIds.length > 0) {
      context.queryClient.prefetchQuery(
        quotationsByIdsQueryOptions(deps.quotationIds),
      );
    }
    return null;
  },
});
